import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { LucideAngularModule } from 'lucide-angular';
import { RMSPlanSummaryService } from '../../../../shared/services/rmsPlanSummary.service';
import { RmsPlanCompletionComponent } from './rms-plan-completion/rms-plan-completion.component';
import { RmsPlanSummarySignatureComponent } from './rms-plan-summary-signature/rms-plan-summary-signature.component';
import { RmsPlanSummarySubmissionComponent } from './rms-plan-summary-submission/rms-plan-summary-submission.component';
import { IRMSPlanSummary } from '../../../../shared/models/rmsPlanSummary';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'rms-plan-summary',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    SpinnerComponent,
    PopupMessageComponent,
    RmsPlanCompletionComponent,
    RmsPlanSummarySignatureComponent,
    RmsPlanSummarySubmissionComponent
],
  templateUrl: './rms-plan-summary.component.html',
  styleUrl: './rms-plan-summary.component.css'
})
export class RmsPlanSummaryComponent {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentRmsPlanSummarytForm: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'rms_plan_completion_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'official_signatures_section_completion',
      nextStepDependency: 'rms_plan_completion_section_completion'
    }
  ];
  finalStepConfig = {
      id: 3,
      completionProperty: 'rms_plan_summary_completion',
      nextStepDependency: 'official_signatures_section_completion'
  };
  steps = [1, 2];
  currentStep: number = 1;
  tooltips = ['Schedule Review', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private rmsPlanSummaryService: RMSPlanSummaryService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadRmsPlanSummaryForm();
  }

  loadRmsPlanSummaryForm(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.rmsPlanSummaryService.getRMSPlanSummaryByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (Object.keys(response).length === 0) {
            this.currentRmsPlanSummarytForm = null;
            this.loading = false;
            return
          } else {
            this.currentRmsPlanSummarytForm = response;
            this.loading = false;
            return
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleUpdateRmsPlanSumamryForm(data: IRMSPlanSummary, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.rmsPlanSummaryService.updateRMSPlanSummary(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadRmsPlanSummaryForm();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 3 ) {
            this.router.navigate(['/plans/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating rms plan summary:', error);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/plans/']);
  }
  
  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentRmsPlanSummarytForm) {
      return this.currentRmsPlanSummarytForm?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }


}
